// Central design tokens — shared brand palette used across the MUI theme,
// SCSS partials, and any component that needs a raw color value outside
// the theme (canvases, SVG fills, inline gradients, etc.)
import { alpha } from "@mui/material";

export const colors = {
  navy: "#193444",
  navyDeep: "#0f2530",
  navyMid: "#274b60",
  blue: "#2175b2",
  blueHover: "#185f92",
  sky: "#48caf2",
  lime: "#c8e955",
  limeDeep: "#b3d43f",
  bg: "#f4f7f9",
  surface: "#ffffff",
  line: "rgba(25, 52, 68, 0.1)",
  muted: "#6b7f89",
  /** Cold/hot ends of the heatmap-chart intensity scale — a conventional
   * blue-to-red data-viz gradient, kept independent of the brand hues so
   * "low vs. high" stays readable regardless of light/dark mode. */
  heatmapCold: "#91a4e4",
  heatmapHot: "#ff3700",
  /** @deprecated use `navy` — kept for backwards compatibility */
  darkBlue: "#193444",
};

export const shadows = {
  card: "0 12px 28px -14px rgba(25, 52, 68, 0.22)",
  cardHover: "0 18px 36px -14px rgba(25, 52, 68, 0.3)",
  raised: "0 4px 14px -6px rgba(25, 52, 68, 0.28)",
  /** Deep drop shadow for a card floating over the always-dark sign-in hero. */
  heroCard: `0 24px 48px -20px ${alpha("#000000", 0.55)}`,
};

/**
 * A handful of the app's surfaces (sidebar, settings menu, sign-in view) are
 * intentionally dark regardless of the active light/dark theme. These two
 * helpers name the sky-tint and white-tint values used for hover/active
 * states and hairline borders on that "always-dark" chrome, so the same
 * rgba() literal isn't retyped by hand in every file that needs it.
 */
export const skyTint = (opacity: number) => alpha(colors.sky, opacity);
export const whiteTint = (opacity: number) => alpha("#ffffff", opacity);

/**
 * Named content-width tiers for top-level views, so each page's `Container`/
 * `maxWidth` cap is a deliberate choice from a shared vocabulary instead of
 * an arbitrary one-off value. Different pages legitimately want different
 * widths (a simple profile form vs. a chart-dense reporting page), so this
 * intentionally isn't a single constant — just named ones.
 */
export const contentMaxWidth = {
  /** Simple, mostly-form pages (e.g. Profile). */
  form: "lg" as const,
  /** Chart/table-dense pages (e.g. Reporting). */
  data: "xl" as const,
  /** Wide admin matrices that need more than `lg` but not full `xl` (e.g. Capabilities). */
  wide: 1400,
};

export const radii = {
  sm: 8,
  md: 12,
  lg: 18,
};

export const fontStacks = {
  display: "'Montserrat', 'Roboto', sans-serif",
  body: "'Outfit', 'Roboto', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

export const styleConstants = {
  mobileSidebarHeight: "56px",
  // Height of the fixed bottom action bar (Scan/Filter/Search/Add) on mobile
  // asset/ticket/user table views — see components/data-table/mobile-menu.
  mobileTableMenuHeight: "53px",
};

/**
 * Full-bleed page height for top-level table/form views. On mobile the fixed
 * top bar in SidebarLayout (styleConstants.mobileSidebarHeight) sits above the
 * view in normal flow, so a plain `minHeight: "100vh"` view stacked below it
 * makes the whole page taller than the viewport (a second, page-level scroll
 * on top of the DataTable's own internal scroll). Using an exact `height`
 * that subtracts the bar on mobile — and the true 100vh once the bar moves
 * into the desktop sidebar — keeps each view exactly one viewport tall.
 */
export const fullBleedViewSx = {
  height: { xs: `calc(100vh - ${styleConstants.mobileSidebarHeight})`, md: "100vh" },
};
